import React, { useState, Children } from 'react';
import { ChevronRightIcon, ChevronDownIcon, PlusIcon, ArrowRightIcon, SettingsIcon, UsersIcon, BuildingIcon, StoreIcon, QrCodeIcon, BarChart2Icon, SearchIcon, InfoIcon, ClockIcon, CheckIcon, XIcon } from 'lucide-react';
import Card from '../../components/ui/Card';
import StatusBadge from '../../components/ui/StatusBadge';
import Modal from '../../components/ui/Modal';
// Mock hierarchy data
const hierarchyData = {
  id: 'root',
  name: 'All Merchants',
  type: 'root',
  children: [{
    id: 'SM001',
    name: 'Global Retail Group',
    type: 'superMerchant',
    status: 'active',
    transactionVolume: 1250000,
    merchantCount: 8,
    lastActivity: '2023-07-15T10:30:00Z',
    children: [{
      id: 'CM001',
      name: 'CityMart Stores',
      type: 'chainMerchant',
      status: 'active',
      transactionVolume: 520000,
      storeCount: 24,
      lastActivity: '2023-07-15T09:30:00Z',
      children: [{
        id: 'ST001',
        name: 'CityMart Downtown',
        type: 'store',
        status: 'active',
        transactionVolume: 85000,
        terminalCount: 4,
        lastActivity: '2023-07-15T09:30:00Z',
        children: [{
          id: 'TM001',
          name: 'Terminal 1',
          type: 'terminal',
          status: 'active',
          transactionVolume: 25000,
          lastActivity: '2023-07-15T09:30:00Z'
        }, {
          id: 'TM002',
          name: 'Terminal 2',
          type: 'terminal',
          status: 'active',
          transactionVolume: 22000,
          lastActivity: '2023-07-14T14:45:00Z'
        }]
      }, {
        id: 'ST002',
        name: 'CityMart Uptown',
        type: 'store',
        status: 'active',
        transactionVolume: 72000,
        terminalCount: 3,
        lastActivity: '2023-07-14T15:45:00Z',
        children: []
      }]
    }, {
      id: 'CM002',
      name: 'HomeGoods Plus',
      type: 'chainMerchant',
      status: 'active',
      transactionVolume: 480000,
      storeCount: 15,
      lastActivity: '2023-07-14T15:45:00Z',
      children: []
    }, {
      id: 'IM001',
      name: 'Downtown Electronics',
      type: 'individualMerchant',
      status: 'active',
      transactionVolume: 125000,
      lastActivity: '2023-07-16T10:20:00Z',
      children: []
    }, {
      id: 'IM002',
      name: 'Fashion Forward',
      type: 'individualMerchant',
      status: 'inactive',
      transactionVolume: 85000,
      lastActivity: '2023-07-10T14:15:00Z',
      children: []
    }]
  }, {
    id: 'SM002',
    name: 'Fast Food Alliance',
    type: 'superMerchant',
    status: 'active',
    transactionVolume: 980000,
    merchantCount: 12,
    lastActivity: '2023-07-16T14:45:00Z',
    children: [{
      id: 'CM003',
      name: 'Quick Burger Chain',
      type: 'chainMerchant',
      status: 'active',
      transactionVolume: 620000,
      storeCount: 32,
      lastActivity: '2023-07-16T12:30:00Z',
      children: []
    }, {
      id: 'CM004',
      name: 'Fresh Salad Bars',
      type: 'chainMerchant',
      status: 'active',
      transactionVolume: 240000,
      storeCount: 18,
      lastActivity: '2023-07-15T11:45:00Z',
      children: []
    }, {
      id: 'IM003',
      name: 'Gourmet Coffee Shop',
      type: 'individualMerchant',
      status: 'active',
      transactionVolume: 75000,
      lastActivity: '2023-07-16T08:20:00Z',
      children: []
    }]
  }, {
    id: 'SM003',
    name: 'Tech Distribution Network',
    type: 'superMerchant',
    status: 'inactive',
    transactionVolume: 450000,
    merchantCount: 5,
    lastActivity: '2023-06-30T09:15:00Z',
    children: []
  }]
};
// Mock audit logs
const auditLogsData = [{
  id: '1',
  timestamp: '2023-07-17T10:30:00Z',
  userName: 'John Smith',
  action: 'Moved Merchant',
  details: 'Moved "Premium Phones" from "Tech Distribution Network" to "Global Retail Group"'
}, {
  id: '2',
  timestamp: '2023-07-16T14:45:00Z',
  userName: 'Sarah Johnson',
  action: 'Added Store',
  details: 'Added "CityMart Midtown" to "CityMart Stores"'
}, {
  id: '3',
  timestamp: '2023-07-15T09:30:00Z',
  userName: 'Michael Chen',
  action: 'Updated Configuration',
  details: 'Updated settlement rules for "Fast Food Alliance"'
}, {
  id: '4',
  timestamp: '2023-07-14T16:20:00Z',
  userName: 'Emma Rodriguez',
  action: 'Added Terminal',
  details: 'Added "Terminal 3" to "CityMart Downtown"'
}, {
  id: '5',
  timestamp: '2023-07-13T11:15:00Z',
  userName: 'David Wilson',
  action: 'Created Merchant',
  details: 'Created new merchant "Gourmet Coffee Shop"'
}];
interface HierarchyNode {
  id: string;
  name: string;
  type: string;
  status?: string;
  transactionVolume?: number;
  merchantCount?: number;
  storeCount?: number;
  terminalCount?: number;
  lastActivity?: string;
  children: HierarchyNode[];
}
interface AuditLog {
  id: string;
  timestamp: string;
  userName: string;
  action: string;
  details: string;
}
const HierarchyView: React.FC = () => {
  const [expandedNodes, setExpandedNodes] = useState<Record<string, boolean>>({
    root: true,
    SM001: true,
    CM001: true,
    ST001: true
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedNode, setSelectedNode] = useState<HierarchyNode | null>(null);
  const [showNodeDetailModal, setShowNodeDetailModal] = useState(false);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [showAuditLogs, setShowAuditLogs] = useState(false);
  const [auditLogs] = useState<AuditLog[]>(auditLogsData);
  const toggleNode = (nodeId: string) => {
    setExpandedNodes(prev => ({
      ...prev,
      [nodeId]: !prev[nodeId]
    }));
  };
  const handleNodeClick = (node: HierarchyNode) => {
    setSelectedNode(node);
    setShowNodeDetailModal(true);
  };
  const handleReassignNode = () => {
    if (selectedNode) {
      setShowNodeDetailModal(false);
      setShowReassignModal(true);
    }
  };
  const formatDateTime = (dateString?: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const formatCurrency = (amount?: number) => {
    if (amount === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };
  const getStatusBadge = (status?: string) => {
    if (!status) return null;
    switch (status) {
      case 'active':
        return <StatusBadge status="success" text="Active" data-id="element-919" />;
      case 'inactive':
        return <StatusBadge status="danger" text="Inactive" data-id="element-920" />;
      case 'pending':
        return <StatusBadge status="warning" text="Pending" data-id="element-921" />;
      default:
        return <StatusBadge status="default" text={status} data-id="element-922" />;
    }
  };
  const getNodeIcon = (nodeType: string) => {
    switch (nodeType) {
      case 'root':
        return <UsersIcon className="h-5 w-5 text-gray-500" data-id="element-923" />;
      case 'superMerchant':
        return <BuildingIcon className="h-5 w-5 text-blue-500" data-id="element-924" />;
      case 'chainMerchant':
        return <BuildingIcon className="h-5 w-5 text-purple-500" data-id="element-925" />;
      case 'individualMerchant':
        return <BuildingIcon className="h-5 w-5 text-green-500" data-id="element-926" />;
      case 'store':
        return <StoreIcon className="h-5 w-5 text-amber-500" data-id="element-927" />;
      case 'terminal':
        return <QrCodeIcon className="h-5 w-5 text-gray-500" data-id="element-928" />;
      default:
        return <BuildingIcon className="h-5 w-5 text-gray-500" data-id="element-929" />;
    }
  };
  const getNodeTypeLabel = (nodeType: string) => {
    switch (nodeType) {
      case 'root':
        return 'All Merchants';
      case 'superMerchant':
        return 'Super Merchant';
      case 'chainMerchant':
        return 'Chain Merchant';
      case 'individualMerchant':
        return 'Individual Merchant';
      case 'store':
        return 'Store';
      case 'terminal':
        return 'Terminal';
      default:
        return nodeType;
    }
  };
  const renderNode = (node: HierarchyNode, level: number = 0) => {
    const isExpanded = expandedNodes[node.id] || false;
    const hasChildren = node.children && node.children.length > 0;
    // Skip rendering if searching and no match
    if (searchQuery && !node.name.toLowerCase().includes(searchQuery.toLowerCase())) {
      // But check if any children match
      const matchingChildren = node.children.filter(child => renderNode(child, level + 1) !== null);
      if (matchingChildren.length === 0 && node.type !== 'root') {
        return null;
      }
    }
    return <div key={node.id} className="mb-1" data-id="element-930">
        <div className={`flex items-center p-2 rounded-md ${level > 0 ? 'hover:bg-gray-100 dark:hover:bg-gray-700' : ''} ${selectedNode?.id === node.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`} style={{
        paddingLeft: `${level * 1.5 + 0.5}rem`
      }} data-id="element-931">
          {hasChildren ? <button onClick={() => toggleNode(node.id)} className="mr-1 p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600" data-id="element-932">
              {isExpanded ? <ChevronDownIcon className="h-4 w-4 text-gray-500" data-id="element-933" /> : <ChevronRightIcon className="h-4 w-4 text-gray-500" data-id="element-934" />}
            </button> : <div className="w-6" data-id="element-935"></div>}
          <div className={`flex items-center flex-grow p-1 rounded-md ${node.type !== 'root' ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700' : ''}`} onClick={() => node.type !== 'root' && handleNodeClick(node)} data-id="element-936">
            <span className="mr-2" data-id="element-937">{getNodeIcon(node.type)}</span>
            <span className="font-medium" data-id="element-938">{node.name}</span>
            {node.status && <span className="ml-3" data-id="element-939">{getStatusBadge(node.status)}</span>}
            <span className="ml-auto text-sm text-gray-500 dark:text-gray-400" data-id="element-940">
              {node.transactionVolume !== undefined && formatCurrency(node.transactionVolume)}
            </span>
          </div>
        </div>
        {isExpanded && hasChildren && <div className="border-l border-gray-200 dark:border-gray-700 ml-6" data-id="element-941">
            {node.children.map(child => renderNode(child, level + 1))}
          </div>}
      </div>;
  };
  return <div className="space-y-6" data-id="element-942">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between" data-id="element-943">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-944">
          Merchant Hierarchy View
        </h1>
        <div className="mt-3 sm:mt-0 flex flex-wrap gap-2" data-id="element-945">
          <button onClick={() => setShowAuditLogs(!showAuditLogs)} className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-946">
            <ClockIcon className="h-4 w-4 mr-2" data-id="element-947" />
            Audit Logs
          </button>
          <button className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" data-id="element-948">
            <PlusIcon className="h-4 w-4 mr-2" data-id="element-949" />
            Add Merchant
          </button>
        </div>
      </div>
      <div className="flex flex-col md:flex-row gap-4" data-id="element-950">
        <div className="relative flex-grow" data-id="element-951">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none" data-id="element-952">
            <SearchIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" data-id="element-953" />
          </div>
          <input type="text" className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-sm rounded-md block w-full pl-10 p-2.5" placeholder="Search merchants..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-954" />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-955">
        <Card className="md:col-span-3" data-id="element-956">
          <div className="flex items-center justify-between mb-4 pb-2 border-b border-gray-200 dark:border-gray-700" data-id="element-957">
            <h2 className="text-lg font-medium text-gray-900 dark:text-white" data-id="element-958">
              Merchant Hierarchy
            </h2>
            <div className="flex text-sm text-gray-500 dark:text-gray-400" data-id="element-959">
              <div className="flex items-center mr-4" data-id="element-960">
                <div className="w-3 h-3 rounded-full bg-blue-500 mr-1" data-id="element-961"></div>
                <span data-id="element-962">Super Merchant</span>
              </div>
              <div className="flex items-center mr-4" data-id="element-963">
                <div className="w-3 h-3 rounded-full bg-purple-500 mr-1" data-id="element-964"></div>
                <span data-id="element-965">Chain Merchant</span>
              </div>
              <div className="flex items-center" data-id="element-966">
                <div className="w-3 h-3 rounded-full bg-green-500 mr-1" data-id="element-967"></div>
                <span data-id="element-968">Individual Merchant</span>
              </div>
            </div>
          </div>
          <div className="overflow-auto max-h-[calc(100vh-300px)]" data-id="element-969">
            {renderNode(hierarchyData)}
          </div>
        </Card>
        {showAuditLogs ? <Card data-id="element-970">
            <div className="flex items-center justify-between mb-4" data-id="element-971">
              <h2 className="text-lg font-medium text-gray-900 dark:text-white" data-id="element-972">
                Audit Logs
              </h2>
              <button onClick={() => setShowAuditLogs(false)} className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300" data-id="element-973">
                Close
              </button>
            </div>
            <div className="space-y-3 overflow-auto max-h-[calc(100vh-300px)]" data-id="element-974">
              {auditLogs.map(log => <div key={log.id} className="p-3 bg-gray-50 dark:bg-gray-800 rounded-md" data-id="element-975">
                  <div className="flex justify-between items-start" data-id="element-976">
                    <div className="font-medium text-sm" data-id="element-977">{log.action}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400" data-id="element-978">
                      {formatDateTime(log.timestamp)}
                    </div>
                  </div>
                  <div className="text-sm mt-1" data-id="element-979">{log.details}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-2" data-id="element-980">
                    By: {log.userName}
                  </div>
                </div>)}
            </div>
          </Card> : <Card data-id="element-981">
            <div className="flex items-center justify-between mb-4" data-id="element-982">
              <h2 className="text-lg font-medium text-gray-900 dark:text-white" data-id="element-983">
                Quick Stats
              </h2>
            </div>
            <div className="space-y-4" data-id="element-984">
              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md flex items-center" data-id="element-985">
                <BuildingIcon className="h-6 w-6 text-blue-500 mr-3" data-id="element-986" />
                <div data-id="element-987">
                  <div className="text-sm font-medium" data-id="element-988">Super Merchants</div>
                  <div className="text-xl font-semibold" data-id="element-989">3</div>
                </div>
              </div>
              <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-md flex items-center" data-id="element-990">
                <BuildingIcon className="h-6 w-6 text-purple-500 mr-3" data-id="element-991" />
                <div data-id="element-992">
                  <div className="text-sm font-medium" data-id="element-993">Chain Merchants</div>
                  <div className="text-xl font-semibold" data-id="element-994">4</div>
                </div>
              </div>
              <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-md flex items-center" data-id="element-995">
                <BuildingIcon className="h-6 w-6 text-green-500 mr-3" data-id="element-996" />
                <div data-id="element-997">
                  <div className="text-sm font-medium" data-id="element-998">
                    Individual Merchants
                  </div>
                  <div className="text-xl font-semibold" data-id="element-999">5</div>
                </div>
              </div>
              <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-md flex items-center" data-id="element-1000">
                <StoreIcon className="h-6 w-6 text-amber-500 mr-3" data-id="element-1001" />
                <div data-id="element-1002">
                  <div className="text-sm font-medium" data-id="element-1003">Stores</div>
                  <div className="text-xl font-semibold" data-id="element-1004">3</div>
                </div>
              </div>
              <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-md flex items-center" data-id="element-1005">
                <QrCodeIcon className="h-6 w-6 text-gray-500 mr-3" data-id="element-1006" />
                <div data-id="element-1007">
                  <div className="text-sm font-medium" data-id="element-1008">Terminals</div>
                  <div className="text-xl font-semibold" data-id="element-1009">2</div>
                </div>
              </div>
              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md flex items-center" data-id="element-1010">
                <BarChart2Icon className="h-6 w-6 text-blue-500 mr-3" data-id="element-1011" />
                <div data-id="element-1012">
                  <div className="text-sm font-medium" data-id="element-1013">
                    Total Transaction Volume
                  </div>
                  <div className="text-xl font-semibold" data-id="element-1014">
                    {formatCurrency(2680000)}
                  </div>
                </div>
              </div>
            </div>
          </Card>}
      </div>
      {/* Node Detail Modal */}
      <Modal isOpen={showNodeDetailModal} onClose={() => setShowNodeDetailModal(false)} title={selectedNode ? `${getNodeTypeLabel(selectedNode.type)}: ${selectedNode.name}` : ''} size="lg" data-id="element-1015">
        {selectedNode && <div className="space-y-6" data-id="element-1016">
            <div className="flex items-center" data-id="element-1017">
              <div className="p-3 rounded-full bg-gray-100 dark:bg-gray-700 mr-4" data-id="element-1018">
                {getNodeIcon(selectedNode.type)}
              </div>
              <div data-id="element-1019">
                <h3 className="text-lg font-medium" data-id="element-1020">{selectedNode.name}</h3>
                <div className="flex items-center mt-1" data-id="element-1021">
                  {getStatusBadge(selectedNode.status)}
                  <span className="mx-2 text-gray-500 dark:text-gray-400" data-id="element-1022">
                    •
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1023">
                    {getNodeTypeLabel(selectedNode.type)}
                  </span>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1024">
              <div data-id="element-1025">
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3" data-id="element-1026">
                  Details
                </h4>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-2" data-id="element-1027">
                  <div className="flex justify-between" data-id="element-1028">
                    <span className="text-sm font-medium" data-id="element-1029">ID:</span>
                    <span className="text-sm" data-id="element-1030">{selectedNode.id}</span>
                  </div>
                  <div className="flex justify-between" data-id="element-1031">
                    <span className="text-sm font-medium" data-id="element-1032">
                      Transaction Volume:
                    </span>
                    <span className="text-sm" data-id="element-1033">
                      {formatCurrency(selectedNode.transactionVolume)}
                    </span>
                  </div>
                  {selectedNode.merchantCount !== undefined && <div className="flex justify-between" data-id="element-1034">
                      <span className="text-sm font-medium" data-id="element-1035">
                        Merchant Count:
                      </span>
                      <span className="text-sm" data-id="element-1036">
                        {selectedNode.merchantCount}
                      </span>
                    </div>}
                  {selectedNode.storeCount !== undefined && <div className="flex justify-between" data-id="element-1037">
                      <span className="text-sm font-medium" data-id="element-1038">Store Count:</span>
                      <span className="text-sm" data-id="element-1039">{selectedNode.storeCount}</span>
                    </div>}
                  {selectedNode.terminalCount !== undefined && <div className="flex justify-between" data-id="element-1040">
                      <span className="text-sm font-medium" data-id="element-1041">
                        Terminal Count:
                      </span>
                      <span className="text-sm" data-id="element-1042">
                        {selectedNode.terminalCount}
                      </span>
                    </div>}
                  <div className="flex justify-between" data-id="element-1043">
                    <span className="text-sm font-medium" data-id="element-1044">Last Activity:</span>
                    <span className="text-sm" data-id="element-1045">
                      {formatDateTime(selectedNode.lastActivity)}
                    </span>
                  </div>
                </div>
              </div>
              <div data-id="element-1046">
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3" data-id="element-1047">
                  Configuration
                </h4>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-2" data-id="element-1048">
                  <div className="flex justify-between" data-id="element-1049">
                    <span className="text-sm font-medium" data-id="element-1050">MDR Rate:</span>
                    <span className="text-sm" data-id="element-1051">2.5%</span>
                  </div>
                  <div className="flex justify-between" data-id="element-1052">
                    <span className="text-sm font-medium" data-id="element-1053">
                      Settlement Frequency:
                    </span>
                    <span className="text-sm" data-id="element-1054">Daily</span>
                  </div>
                  <div className="flex justify-between" data-id="element-1055">
                    <span className="text-sm font-medium" data-id="element-1056">
                      Transaction Limit:
                    </span>
                    <span className="text-sm" data-id="element-1057">{formatCurrency(10000)}/day</span>
                  </div>
                  <div className="flex justify-between" data-id="element-1058">
                    <span className="text-sm font-medium" data-id="element-1059">Refund Policy:</span>
                    <span className="text-sm" data-id="element-1060">T+1 (24 hours)</span>
                  </div>
                  <div className="flex justify-between" data-id="element-1061">
                    <span className="text-sm font-medium" data-id="element-1062">
                      Payment Methods:
                    </span>
                    <span className="text-sm" data-id="element-1063">POS, QR, Online</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4" data-id="element-1064">
              <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2" data-id="element-1065">
                Quick Actions
              </h4>
              <div className="flex flex-wrap gap-2" data-id="element-1066">
                <button className="px-3 py-1 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 rounded-md text-sm" data-id="element-1067">
                  View Details
                </button>
                <button onClick={handleReassignNode} className="px-3 py-1 bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 rounded-md text-sm" data-id="element-1068">
                  Reassign
                </button>
                <button className="px-3 py-1 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 rounded-md text-sm" data-id="element-1069">
                  Edit Configuration
                </button>
                <button className="px-3 py-1 bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 rounded-md text-sm" data-id="element-1070">
                  View Transactions
                </button>
              </div>
            </div>
            {selectedNode.children && selectedNode.children.length > 0 && <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4" data-id="element-1071">
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3" data-id="element-1072">
                  Child Entities ({selectedNode.children.length})
                </h4>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg overflow-hidden" data-id="element-1073">
                  <div className="max-h-40 overflow-y-auto" data-id="element-1074">
                    {selectedNode.children.map(child => <div key={child.id} className="flex items-center justify-between px-4 py-2 border-b border-gray-200 dark:border-gray-700 last:border-0 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer" onClick={() => {
                setSelectedNode(child);
                setExpandedNodes(prev => ({
                  ...prev,
                  [selectedNode.id]: true
                }));
              }} data-id="element-1075">
                        <div className="flex items-center" data-id="element-1076">
                          <span className="mr-2" data-id="element-1077">
                            {getNodeIcon(child.type)}
                          </span>
                          <span data-id="element-1078">{child.name}</span>
                        </div>
                        <div className="flex items-center" data-id="element-1079">
                          {getStatusBadge(child.status)}
                          <ChevronRightIcon className="h-4 w-4 ml-2 text-gray-400" data-id="element-1080" />
                        </div>
                      </div>)}
                  </div>
                </div>
              </div>}
          </div>}
      </Modal>
      {/* Reassign Node Modal */}
      <Modal isOpen={showReassignModal} onClose={() => setShowReassignModal(false)} title="Reassign Merchant" size="md" footer={<>
            <button onClick={() => setShowReassignModal(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-1082">
              Cancel
            </button>
            <button onClick={() => {
        // Handle reassignment
        setShowReassignModal(false);
      }} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" data-id="element-1083">
              Reassign
            </button>
          </>} data-id="element-1081">
        {selectedNode && <div className="space-y-4" data-id="element-1084">
            <div className="flex items-center" data-id="element-1085">
              <div className="p-2 rounded-full bg-gray-100 dark:bg-gray-700 mr-3" data-id="element-1086">
                {getNodeIcon(selectedNode.type)}
              </div>
              <div data-id="element-1087">
                <p className="font-medium" data-id="element-1088">{selectedNode.name}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1089">
                  {selectedNode.id}
                </p>
              </div>
            </div>
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3 text-sm" data-id="element-1090">
              <div className="flex items-start" data-id="element-1091">
                <InfoIcon className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-1092" />
                <p className="text-blue-700 dark:text-blue-300" data-id="element-1093">
                  Reassigning this{' '}
                  {getNodeTypeLabel(selectedNode.type).toLowerCase()} will move
                  it and all its child entities to a new parent. Transaction
                  history and configurations will be preserved.
                </p>
              </div>
            </div>
            <div data-id="element-1094">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-1095">
                Current Parent
              </label>
              <div className="flex items-center p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-700" data-id="element-1096">
                <BuildingIcon className="h-5 w-5 text-blue-500 mr-2" data-id="element-1097" />
                <span data-id="element-1098">Global Retail Group</span>
              </div>
            </div>
            <div data-id="element-1099">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-1100">
                New Parent <span className="text-red-500" data-id="element-1101">*</span>
              </label>
              <select className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" required data-id="element-1102">
                <option value="" data-id="element-1103">Select New Parent</option>
                <option value="SM002" data-id="element-1104">Fast Food Alliance</option>
                <option value="SM003" data-id="element-1105">Tech Distribution Network</option>
                <option value="SM004" data-id="element-1106">Hospitality Group International</option>
                <option value="SM005" data-id="element-1107">Urban Transport Consortium</option>
              </select>
            </div>
            <div data-id="element-1108">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-1109">
                Reason for Reassignment
              </label>
              <textarea rows={3} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="Enter reason for reassignment (optional)" data-id="element-1110" />
            </div>
            <div className="flex items-center mt-2" data-id="element-1111">
              <input id="confirm-reassign" type="checkbox" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-1112" />
              <label htmlFor="confirm-reassign" className="ml-2 block text-sm text-gray-700 dark:text-gray-300" data-id="element-1113">
                I confirm this reassignment and understand it will be logged in
                the audit trail
              </label>
            </div>
          </div>}
      </Modal>
    </div>;
};
export default HierarchyView;